import React from "react"
import { Stack, Typography, useTheme } from "@carrotfertility/carotene-core"
import { Form, FormButton } from "@carrotfertility/carotene-core-x"
import { CustomModule, FormField, fieldName, isInput, toDefaultValue } from "./FormField"
import { HealthieAnswer, serializeAnswers } from "./answers"

interface IntakeFormProps {
  form: { name: string | null; custom_modules: CustomModule[] }
  // Receives the serialized answers on submit (host persists via BFF submitForm).
  // Omit for read/fill-only — no submit button is rendered.
  onSubmit?: (answers: HealthieAnswer[]) => void | Promise<void>
  submitLabel?: string
}

const byPosition = (a: CustomModule, b: CustomModule): number => (a.position ?? 0) - (b.position ?? 0)

// Seed a default per control so react-hook-form fields are controlled from first render.
function buildDefaultValues(modules: CustomModule[]): Record<string, unknown> {
  const defaults: Record<string, unknown> = {}
  for (const mod of modules) {
    if (isInput(mod)) defaults[fieldName(mod)] = toDefaultValue(mod, "")
  }
  return defaults
}

export function IntakeForm({ form, onSubmit, submitLabel = "Submit" }: IntakeFormProps): JSX.Element {
  const theme = useTheme()
  const modules = [...form.custom_modules].sort(byPosition)

  return (
    <Form
      defaultValues={buildDefaultValues(modules)}
      onSubmit={(values) => onSubmit?.(serializeAnswers(modules, values as Record<string, unknown>))}
    >
      <Stack spacing={theme.tokens.spacing.md}>
        {form.name ? <Typography variant="h2">{form.name}</Typography> : null}
        {modules.map((mod) => (
          <FormField key={mod.id} mod={mod} />
        ))}
        {onSubmit ? (
          <FormButton type="submit" sx={{ alignSelf: "flex-start" }}>
            {submitLabel}
          </FormButton>
        ) : null}
      </Stack>
    </Form>
  )
}
